import cartesianProduct from 'just-cartesian-product'

/**
 * Service that handles report data file operations
 */
export class ReportDataFileService {
  /**
   * Get one or more data filenames based on the params
   *
   * @param {object} report Report
   * @param {string} strata Stratification subgroup key
   * @param {string} slider Slider subgroup key
   * @param {object} filters Filters to filter subgroups by
   * @param {string} dataKey Report data key
   * @returns {string[]} Filenames
   */
  getFilenames (report, strata, slider, filters = {}, dataKey) {
    const filenames = []
    const dataSet = report.parent?.dataSets[dataKey]

    const subgroups = dataSet?.subgroups || []
    if (subgroups.length === 0) {
      return [`${dataKey}/data.json`]
    }

    if (dataSet) {
      this.getProductOfSubgroupValues(
        dataSet, strata, slider, filters
      ).forEach((valueIndices) => {
        filenames.push(
          `${dataKey}/${valueIndices.join('-')}.json`
        )
      })
    }

    return filenames
  }

  /**
   * Get a data file filename based on report, strata, slider, filters, and dataKey
   *
   * Example:
   *
   *   Params:
   *     dataSet: {
   *        subgroups: [
   *          'region',
   *          'age',
   *          'sex'
   *        ],
   *        subgroupValues: {
   *          region: ['Northeast', 'Southeast', 'Northwest', 'Southwest'],
   *          age: ['18-45', '45-55', '55-65', '65-75', '85+']
   *          sex: ['Male', 'Female'],
   *        }
   *      },
   *      strata: 'region'
   *      slider: 'age'
   *      Filter: { sex: 'Female' }
   *
   *   Return:
   *     Cartesian product of all possible strata, all possible slider values, and selected filter values
   *
   *     [
   *        [0, 0, 1], [0, 1, 1], [0, 2, 1], [0, 3, 1], [0, 4, 1],
   *        [1, 0, 1], [1, 1, 1], [1, 2, 1], [1, 3, 1], [1, 4, 1],
   *        [2, 0, 1], [2, 1, 1], [2, 2, 1], [2, 3, 1], [2, 4, 1],
   *        [3, 0, 1], [3, 1, 1], [3, 2, 1], [3, 3, 1], [3, 4, 1]
   *     ]
   *
   * @param {string} dataKey Report dataset
   * @param {string} strata Stratification subgroup key
   * @param {string} slider Slider subgroup key
   * @param {object} filters Filters to filter subgroups by
   * @returns {Number[][]} Array subgroup combinations
   */
  getProductOfSubgroupValues (dataSet, strata, slider, filters = {}) {
    const filterKeys = Object.keys(filters)
    const subgroups = dataSet.subgroups || []
    const subgroupValues = dataSet.subgroupValues || {}

    const subgroupIndices = subgroups.map((subgroup) => {
      const subgroupValue = subgroupValues[subgroup]

      let valueIndices
      const includeAllValues = subgroup === strata || subgroup === slider
      if (includeAllValues) {
        valueIndices = subgroupValue.reduce((valueIndices, value, index) => {
          if (value !== null) {
            valueIndices.push(index)
          }

          return valueIndices
        }, [])
      } else {
        const filterKey = filterKeys.find((key) => key === subgroup)
        const filterValue = filterKey ? filters[filterKey] : null
        const valueIndex = subgroupValue.findIndex((value) => value === filterValue)
        valueIndices = (valueIndex >= 0) ? [valueIndex] : []
      }

      return valueIndices
    })

    return cartesianProduct(subgroupIndices)
  }
}

export const reportDataFileService = new ReportDataFileService()
